import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Formik } from 'formik';
import Button from '../components/Button';
import { Modal, ModalEditableBody, ModalPart } from '../components/Modal';
import { useToast } from '../components/Toast';
import { createCommentCache } from '../utilities/cache';
import { CREATE_COMMENT } from '../utilities/mutations';

const NewCommentModal = ({ topicId, onClose }) => {
  const params = useParams();
  const toast = useToast();
  const [createComment] = useMutation(CREATE_COMMENT);
  const [isUnsaved, setIsUnsaved] = useState(false);
  const [shake, setShake] = useState(false);

  const modalRef = useRef();
  const [scrollFix, setScrollFix] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);

  const [submitting, setSubmitting] = useState(false);

  const closeModal = () => {
    if (!shake && !submitting) {
      if (isUnsaved) {
        setShake(true);
        toast({ children: `Don't forget to save!` });
        setTimeout(() => setShake(false), 500);
      } else {
        if (onClose) {
          onClose();
        }
      }
    }
  };

  const close = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <Modal
      ref={modalRef}
      shake={shake}
      onClose={() => closeModal()}
      onScroll={() => {
        if (scrollFix) {
          setScrollFix(false);
          modalRef.current.scrollTop = scrollTop;
        } else {
          setScrollTop(modalRef.current.scrollTop);
        }
      }}
    >
      <Formik
        initialValues={{ body: '' }}
        validate={(touched) => {
          if (touched.body !== '') {
            setIsUnsaved(true);
          } else {
            setIsUnsaved(false);
          }
        }}
        onSubmit={async (values) => {
          try {
            setSubmitting(true);
            await createComment({
              variables: {
                body: values.body,
                topicId: topicId,
              },
              update: (cache, { data: { createComment } }) =>
                createCommentCache(topicId, cache, createComment),
            });
            close();
            setSubmitting(false);
          } catch (error) {
            toast({ children: error.message });
            setSubmitting(false);

            return;
          }
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <ModalEditableBody
              type="text"
              name="body"
              setScrollFix={setScrollFix}
              scrollTop={scrollTop}
              disabled={isSubmitting}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.body}
              modalRef={modalRef}
            />
            <ModalPart>
              <Button
                type="button"
                disabled={isSubmitting}
                onClick={() => close()}
                isExtended
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={values.body.trim() === '' || isSubmitting}
                isPrimary
                isExtended
              >
                Reply
              </Button>
            </ModalPart>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default NewCommentModal;
